import { useState, useEffect } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Dashboard } from './pages/Dashboard';
import { ThemeProvider } from './components/ThemeProvider';
import { ConnectionStatus } from './components/ConnectionStatus';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ViewProvider } from './context/ViewContext';

// Import diagnostic tools for development
if (import.meta.env.DEV) {
  import('./debug/tslDiagnostic');
}

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      staleTime: 5000,
      refetchOnWindowFocus: false,
    },
  },
});

// Make QueryClient available globally for debugging
if (import.meta.env.DEV && typeof window !== 'undefined') {
  (window as any).queryClient = queryClient;
}

function App() {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('theme');
    return saved ? saved === 'dark' : import.meta.env.VITE_DEFAULT_THEME === 'dark';
  });

  useEffect(() => {
    localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);

  const toggleTheme = () => setIsDarkMode(!isDarkMode);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider isDarkMode={isDarkMode} toggleTheme={toggleTheme}>
          <ViewProvider>
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors">
              <ConnectionStatus />
              <Dashboard />
            </div>
          </ViewProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
